import React, { useState, useEffect } from 'react';
import './BuyerMarketplace.scss';
import { Breadcrumb, BreadcrumbItem, Card } from 'reactstrap';
import { HomeButton } from '../../../components/HomeButton/home-button';
import Layout from '../../../components/Layout';
import MarketplaceOpportunities from './MarketplaceOpportunities/MarketplaceOpportunities';
import BuyerProfile from './BuyerProfile/BuyerProfile';
import { OpportunityDetails } from './OpportunityDetails/OpportunityDetails';
import { useStateSelector } from '../../../store/selectors';
import { GetBuyer } from '../../../slices/marketplace/buyer/thunks/get-buyer.thunk';
import { useDispatch } from 'react-redux';
import ContactSupportButton from '../../ContactSupportButton/ContactSupportButton';
import { SupportContextEnum } from '../../../support-portal/models/SupportContext';
import MasterDeadlineDocket from './MasterDeadlineDocket/MasterDeadlineDocket';
import { MyTeamMembers } from './MyTeamMembers/MyTeamMembers';
import ConnectMarketplaceAgreements from './ConnectMarketplaceAgreements/ConnectMarketplaceAgreements';
import ConnectMarketplaceTemplates from './ConnectMarketplaceTemplates/ConnectMarketplaceTemplates';
import CurrentProcessCommercialTerms from './CurrentProcessCommercialTerms/CurrentProcessCommercialTerms';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { USER_ROLES } from '../../../utils/constants';
import { FeatureFlagsEnum } from '../../FeatureFlags/FeatureFlags.enum';
import { useFeatureFlag } from '../../../custom-hooks/useFeatureFlag';
import { BuyerMarketplaceTab } from '../../../slices/marketplace/timeline/models/enums/buyer-marketplace-tab.enum';
import vvConnectLogo from '../../../assets/demo-ui/images/VetValueConnect.png';
import {
    TeamMemberPermissions,
    TeamMemberPermissionsHelper,
} from './MyTeamMembers/TeamMemberPermissionsHelper';
import {
    TeamMemberType,
    InternalTeamMemberRole,
    ExternalPermissions,
} from './MyTeamMembers/Models/Enums';
import { NotificationFlag } from '../../../components/NotificationFlag/NotificationFlag';
import { NotificationFlagsCategories } from '../../../models/NotificationFlags/NotificationFlagsCategories';
import { RefreshNotificationFlags } from '../../../slices/notification-flags/refresh-notification-flags';
import { BuyerNdaGate } from './BuyerNdaGate/BuyerNdaGate';
import { BuyerNdaReminderPopup } from './BuyerNdaReminderPopup/BuyerNdaReminderPopup';
import { BuyersNdaStatusResponse } from '../../../slices/marketplace/timeline/models/buyers-nda-status.model';
import { EngagementMeetingsScheduling } from '../../ConnectMarketplaceDashboard/EngagementMeetingsScheduling/EngagementMeetingsScheduling';
import { TimelineParty } from '../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { EngagementMeeting } from '../../../slices/marketplace/meetings/models/get-engagement-conference';
import createNotification from '../../../utils/createNotification';
import useViewportWidth from '../../../custom-hooks/useViewportWidth';

const BuyerMarketplace = () => {
    const [activeTab, setActiveTab] = useState(
        BuyerMarketplaceTab.Opportunities
    );
    const isFF_MrktplcBuyers_3_6Enabled = useFeatureFlag(
        FeatureFlagsEnum.FF_MrktplcBuyers_3_6
    );
    const [searchParams] = useSearchParams();
    const [refreshOpportunitiesToggle, setRefreshOpportunitiesToggle] =
        useState(false);
    const navigate = useNavigate();
    const selectedOpportunity = useStateSelector(
        (state) => state.opportunities.selectedOpportuity
    );

    const dispatch = useDispatch() as any;
    const axios = useStateSelector((state) => state.core.axios);

    const [ndaStatus, setNdaStatus] = useState<BuyersNdaStatusResponse | null>(null);
    const [isNdaCheckCompleted, setIsNdaCheckCompleted] = useState(false);
    const [isNdaReminderPopupOpen, setIsNdaReminderPopupOpen] = useState(false);

    const [engagementMeetings, setEngagementMeetings] = useState<
        EngagementMeeting[]
    >([]);
    const [meetingsRefreshKey, setMeetingsRefreshKey] = useState(0);

    const refreshMeetings = () => {
        axios
            .get('/api/Marketplace/MarketplaceMeetings')
            .then((response) => {
                if (response.status === 200) {
                    const meetings = response.data || [];
                    setEngagementMeetings(meetings);
                    setMeetingsRefreshKey((prev) => prev + 1);
                } else {
                    setEngagementMeetings([]);
                    createNotification('Failed to fetch meetings', 'error');
                }
            })
            .catch((error) => {
                console.log(error);
                setEngagementMeetings([]);
            });
    };

    useEffect(() => {
        refreshMeetings();
    }, []);

    const handleNdaSigned = () => {
        setNdaStatus((prev) =>
            prev ? { ...prev, hasSignedLatestNda: true } : prev
        );
    };

    const tabComponents = [
        {
            id: BuyerMarketplaceTab.Opportunities,
            label: 'Marketplace Opportunities',
            content: (
                <MarketplaceOpportunities
                    refreshOpportunitiesToggle={refreshOpportunitiesToggle}
                />
            ),
            notificationFlag: `${NotificationFlagsCategories.MpCode}.${NotificationFlagsCategories.OpportunitiesCode}`,
        },
        {
            id: BuyerMarketplaceTab.DeadlineDocket,
            label: 'Deadline Docket',
            content: (
                <MasterDeadlineDocket
                    setActiveTab={setActiveTab}
                    selectedOpportunityId={selectedOpportunity.opportunityId}
                />
            ),
            notificationFlag: `${NotificationFlagsCategories.MpCode}.${NotificationFlagsCategories.MasterDeadlineDocketCode}`,
        },
        {
            id: BuyerMarketplaceTab.Profile,
            label: 'Buyer Profile',
            content: <BuyerProfile />,
        },
        {
            id: BuyerMarketplaceTab.MyTeamMembers,
            label: 'My Team Members',
            content: <MyTeamMembers />,
        },
        {
            id: BuyerMarketplaceTab.Meetings,
            label: 'Meetings',
            content: (
                <EngagementMeetingsScheduling
                    key={meetingsRefreshKey}
                    engagementId={selectedOpportunity.opportunityId}
                    party={TimelineParty.Buyer}
                    meetings={engagementMeetings}
                    mode="buyerTopLevel"
                    onRefreshMeetings={refreshMeetings}
                />
            ),
            notificationFlag: `${NotificationFlagsCategories.MpCode}.${NotificationFlagsCategories.GlobalCommsCode}`,
        },
        {
            id: BuyerMarketplaceTab.ConnectMarketplaceAgreements,
            label: (
                <span>
                    <i>Connect</i> Agreements
                </span>
            ),
            content: <ConnectMarketplaceAgreements ndaStatus={ndaStatus} onNdaSigned={handleNdaSigned} />,
            notificationFlag: `${NotificationFlagsCategories.MpCode}.${NotificationFlagsCategories.ConnectAgreementsCode}`,
        },
        {
            id: BuyerMarketplaceTab.ConnectMarketplaceTemplates,
            label: (
                <span>
                    <i>Connect</i> Document Templates
                </span>
            ),
            content: <ConnectMarketplaceTemplates />,
        },
        {
            id: BuyerMarketplaceTab.CurrentProcessCommercialTerms,
            label: (
                <span>
                    <i>Connect</i> Fee Schedule
                </span>
            ),
            content: <CurrentProcessCommercialTerms showDateModified={true} />,
        },
    ];

    const fetchBuyerProfile = async () => {
        await dispatch(GetBuyer(null));
    };
    const buyerProfile = useStateSelector(
        (state) => state.buyersState.targetBuyer
    );
    useEffect(() => {
        const opportunityIdParam = searchParams.get('opportunityId');
        if (opportunityIdParam) {
            return;
        }

        const tabParam = searchParams.get('tab');

        if (tabParam === 'my-team-members') {
            setActiveTab(BuyerMarketplaceTab.MyTeamMembers);
            navigate('/opportunities', { replace: true });
        } else if (tabParam === 'dashboard') {
            setActiveTab(BuyerMarketplaceTab.Opportunities);
            navigate('/opportunities', { replace: true });
        } else {
            const numberValue = Number(tabParam);
            const validTabValues = [
                BuyerMarketplaceTab.Opportunities,
                BuyerMarketplaceTab.DeadlineDocket,
                BuyerMarketplaceTab.Profile,
                BuyerMarketplaceTab.MyTeamMembers,
                BuyerMarketplaceTab.Meetings,
                BuyerMarketplaceTab.ConnectMarketplaceAgreements,
                BuyerMarketplaceTab.ConnectMarketplaceTemplates,
                BuyerMarketplaceTab.CurrentProcessCommercialTerms,
            ];
            if (
                tabParam &&
                !isNaN(numberValue) &&
                validTabValues.includes(numberValue)
            ) {
                setActiveTab(numberValue);
            }
        }
    }, [searchParams, navigate]);

    useEffect(() => {
        fetchBuyerProfile();
    }, []);

    const checkBuyerNdaStatus = () => {
        axios
            .get('/api/Marketplace/MarketplaceBuyersNda')
            .then((response) => {
                const status: BuyersNdaStatusResponse = response.data;
                setNdaStatus(status);
                setIsNdaCheckCompleted(true);

                const isAdmin = summary?.roleId === USER_ROLES.ADMIN;
                const shouldShowReminder =
                    status.isReturningBuyer &&
                    !status.hasSignedLatestNda &&
                    !isAdmin;
                if (shouldShowReminder) {
                    setIsNdaReminderPopupOpen(true);
                }
            })
            .catch((error) => {
                console.log(error);
                setNdaStatus(null);
                setIsNdaCheckCompleted(true);
            });
    };

    useEffect(() => {
        checkBuyerNdaStatus();
    }, []);

    const isTabAvailable = (tab: BuyerMarketplaceTab) => {
        if (tab === BuyerMarketplaceTab.ConnectMarketplaceTemplates) {
            if (summary?.roleId === USER_ROLES.ADMIN) {
                return true;
            }
            const requiredPermissions = {
                isPrimaryBuyer: false,
                type: TeamMemberType.Internal,
                role: InternalTeamMemberRole.CorpDev,
                permission: null,
            } as TeamMemberPermissions;

            const currentPermissions = {
                isPrimaryBuyer:
                    buyerProfile?.teamMemberRole?.isPrimaryBuyer || false,
                type: buyerProfile?.teamMemberRole?.teamMemberType,
                role: buyerProfile?.teamMemberRole?.internalTeamMemberRole,
                permission: null,
            } as TeamMemberPermissions;

            return TeamMemberPermissionsHelper.isAvailable(
                currentPermissions,
                requiredPermissions
            );
        }

        const isActingPrimaryBuyer = buyerProfile?.teamMemberRole?.isPrimaryBuyer || false;
        if (summary && summary.roleId === USER_ROLES.PRACTICE_SECONDARY_USER && !isActingPrimaryBuyer) {
            if (tab === BuyerMarketplaceTab.Profile) {
                const requiredPermissions = {
                    isPrimaryBuyer: false,
                    type: TeamMemberType.Internal,
                    role: null,
                    permission: null,
                } as TeamMemberPermissions;

                const currentPermissions = {
                    isPrimaryBuyer: false,
                    type: buyerProfile?.teamMemberRole?.teamMemberType,
                    role: null,
                    permission: null,
                } as TeamMemberPermissions;

                const result = TeamMemberPermissionsHelper.isAvailable(
                    currentPermissions,
                    requiredPermissions
                );
                return result;
            } else if (
                tab === BuyerMarketplaceTab.CurrentProcessCommercialTerms
            ) {
                const requiredPermissions = {
                    isPrimaryBuyer: false,
                    type: TeamMemberType.Internal,
                    role: InternalTeamMemberRole.CorpDev,
                    permission: null,
                } as TeamMemberPermissions;
                const currentPermissions = {
                    isPrimaryBuyer:
                        buyerProfile?.teamMemberRole?.isPrimaryBuyer,
                    type: buyerProfile?.teamMemberRole?.teamMemberType,
                    role: buyerProfile?.teamMemberRole?.internalTeamMemberRole,
                    permission: null,
                } as TeamMemberPermissions;
                return TeamMemberPermissionsHelper.isAvailable(
                    currentPermissions,
                    requiredPermissions
                );
            } else if (tab === BuyerMarketplaceTab.Meetings) {
                if (summary.roleId !== USER_ROLES.PRACTICE_SECONDARY_USER) {
                    return true;
                }
                if (summary?.associateMember?.length) {
                    const mappings =
                        summary.associateMember[0]
                            ?.associateMemberMarketplaceMappings;
                    if (mappings) {
                        const hasRestrictedMapping = mappings.some(
                            (mapping: any) =>
                                (mapping.opportunityId === null ||
                                    mapping.opportunityId === undefined) &&
                                mapping.type === TeamMemberType.External &&
                                mapping.externalPermissions ===
                                    ExternalPermissions.Vdr
                        );
                        if (hasRestrictedMapping) {
                            return false;
                        }
                    }
                }
                return true;
            } else {
                return (
                    tab === BuyerMarketplaceTab.Opportunities ||
                    tab === BuyerMarketplaceTab.ConnectMarketplaceAgreements
                );
            }
        }
        switch (tab) {
            case BuyerMarketplaceTab.MyTeamMembers:
                return isFF_MrktplcBuyers_3_6Enabled;
            default:
                return true;
        }
    };

    function renderTabContent() {
        const activeTabData = tabComponents.find((tab) => tab.id === activeTab);
        return (
            activeTabData?.content || (
                <div className="d-flex align-items-center justify-content-center mt-4">
                    <h1>Empty Tab</h1>
                </div>
            )
        );
    }
    const summary = useStateSelector((state) => state.userSummary.summary);

    const getTabNotificationFlag = (tab: number) => {
        const targetCode = tabComponents.find((t) => t.id === tab);
        return targetCode?.notificationFlag ? (
            <>
                <NotificationFlag
                    flagClassName="notification-flag-wrapper"
                    eventCode={targetCode.notificationFlag}
                    includePrefix={true}
                    doNotRemove={tab === BuyerMarketplaceTab.ConnectMarketplaceAgreements}
                />
            </>
        ) : (
            <></>
        );
    };

    const renderTabs = () => {
        return (
            <Card className="main-card">
                <div className="body_white_box practice-page-tabs m-0">
                    <div className="body_inner_content">
                        <div className="RRT__container">
                            <div className="RRT__tabs body-tabs">
                                {tabComponents
                                    ?.filter((x) => isTabAvailable(x.id))
                                    .map((item) => {
                                        return (
                                            <div
                                                key={item.id}
                                                className={
                                                    activeTab === item.id
                                                        ? 'RRT__tab RRT__tab--first RRT__tab--selected'
                                                        : 'RRT__tab'
                                                }
                                                onClick={async () => {
                                                    setActiveTab(item.id);
                                                    await dispatch(
                                                        RefreshNotificationFlags()
                                                    );
                                                }}>
                                                {item.label}
                                                {getTabNotificationFlag(
                                                    item.id
                                                )}
                                            </div>
                                        );
                                    })}
                            </div>
                        </div>
                        {renderTabContent()}
                    </div>
                </div>
            </Card>
        );
    };

    const renderOpportunityDetails = () => {
        return (
            <OpportunityDetails
                refreshOpportunities={() => {
                    setRefreshOpportunitiesToggle(!refreshOpportunitiesToggle);
                }}
            />
        );
    };

    const viewportWidth = useViewportWidth();

    const getSupportButtonText = () => {
        return viewportWidth >= 400 
        ? 'Contact Administrator' : 'Support';
    };

    const renderHeader = (showContactSupport = false) => {
        return (
            <div className="main-title-content">
                <div className="title-breadcrumb">
                    <div>
                        <div className="page-title">
                            <h3>Marketplace Dashboard</h3>
                        </div>
                        <Breadcrumb>
                            <BreadcrumbItem>
                                <HomeButton />
                            </BreadcrumbItem>
                            <BreadcrumbItem active>
                                Marketplace Dashboard
                            </BreadcrumbItem>
                        </Breadcrumb>
                    </div>
                    <div className={`vvconnect-logo`}>
                        <img width={165} alt="VVConnect" src={vvConnectLogo} />
                    </div>

                    <div className="buttons-wrapper admin-button-wrapper">
                        {showContactSupport && (
                            <ContactSupportButton
                                supportContext={SupportContextEnum.Marketplace}
                                buttonType="reference"
                                buttonText={getSupportButtonText()}
                                tooltipTargetId="buyer-marketplace-header-support-button"
                                tooltipText="Contact Administrator"
                            />
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const renderMarketplacePage = () => {
        const isAdmin = summary?.roleId === USER_ROLES.ADMIN;
        const isNdaSigned = ndaStatus?.hasSignedLatestNda ?? false;
        const isReturningBuyer = ndaStatus?.isReturningBuyer ?? false;

        if (!isNdaCheckCompleted) {
            return <div className="buyer-marketplace">{renderHeader()}</div>;
        }

        const isReturningBuyerWithUnsignedNda = isReturningBuyer && !isNdaSigned;

        if (!isNdaSigned && !isAdmin && !isReturningBuyerWithUnsignedNda) {
            return (
                <div className="buyer-marketplace">
                    {renderHeader()}
                    <Card className="main-card nda-gate-card">
                        <BuyerNdaGate onNdaSigned={handleNdaSigned} />
                    </Card>
                </div>
            );
        }

        return (
            <div className="buyer-marketplace">
                {renderHeader(true)}

                {isReturningBuyerWithUnsignedNda && (
                    <BuyerNdaReminderPopup
                        open={isNdaReminderPopupOpen}
                        onClose={() => setIsNdaReminderPopupOpen(false)}
                        onNavigateToAgreements={() => {
                            setIsNdaReminderPopupOpen(false);
                            setActiveTab(
                                BuyerMarketplaceTab.ConnectMarketplaceAgreements
                            );
                        }}
                        ndaPostedAt={ndaStatus?.ndaPostedAt ?? null}
                    />
                )}

                {renderTabs()}
                {selectedOpportunity?.opportunityId > 0 &&
                activeTab === BuyerMarketplaceTab.Opportunities ? (
                    renderOpportunityDetails()
                ) : (
                    <></>
                )}
            </div>
        );
    };

    return <Layout>{renderMarketplacePage()}</Layout>;
};

export default BuyerMarketplace;
